import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SocialAuthButtons from '../components/SocialAuthButtons';
import { EnvelopeIcon, LockClosedIcon, UserIcon, CheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const Register = () => {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login: authLogin, checkAuth } = useAuth();

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Применить или оплатить тариф после регистрации
    const applyPendingTariff = async (token) => {
        const pendingTariffStr = localStorage.getItem('pending_tariff');
        if (!pendingTariffStr) return null;
        
        try {
            const tariff = JSON.parse(pendingTariffStr);
            localStorage.removeItem('pending_tariff');
            
            if (tariff.price_per_month === 0) {
                // Бесплатный тариф - применяем сразу
                const res = await fetch('/api/payments/tariffs/change', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tariff_id: tariff.id })
                });
                if (res.ok) {
                    toast.success('Тариф активирован!');
                }
                return { redirect: '/profile' };
            } else {
                // Платный тариф - создаём платёж
                const res = await fetch('/api/payments/create', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        amount: tariff.price_per_month, 
                        tariff_id: tariff.id, 
                        return_url: window.location.origin + '/profile' 
                    })
                });
                const data = await res.json();
                if (data.confirmation_url) {
                    return { paymentUrl: data.confirmation_url };
                }
            }
        } catch (e) {
            console.error('Error applying tariff:', e);
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!username || !email || !password || !confirmPassword) {
            setError('Все поля обязательны для заполнения');
            return;
        }
        if (username.length < 3) {
            setError('Имя пользователя должно содержать минимум 3 символа');
            return;
        }
        if (!validateEmail(email)) {
            setError('Пожалуйста, введите корректный email адрес');
            return;
        }
        if (password.length < 6) {
            setError('Пароль должен содержать минимум 6 символов');
            return;
        }
        if (password !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setLoading(true);
        try {
            // Регистрация с автоматическим входом
            const res = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, email, password })
            });
            const data = await res.json();
            
            if (!res.ok) {
                throw new Error(data.error || 'Ошибка регистрации');
            }
            
            // Если сервер вернул токены - автоматический вход
            if (data.access_token) {
                localStorage.setItem('access_token', data.access_token);
                localStorage.setItem('refresh_token', data.refresh_token);
                
                // Проверяем и применяем отложенный тариф
                const tariffResult = await applyPendingTariff(data.access_token);
                
                // Обновляем контекст авторизации (загружаем user/limits)
                await checkAuth();
                
                if (tariffResult?.paymentUrl) {
                    window.location.href = tariffResult.paymentUrl;
                    return;
                }
                
                toast.success('Регистрация успешна!');
                navigate(tariffResult?.redirect || '/scenarios');
            } else {
                // Старое поведение - переход на логин
                navigate('/login', { state: { message: 'Регистрация успешна! Теперь вы можете войти.' } });
            }
        } catch (err) {
            setError(err.message || 'Произошла ошибка при регистрации');
        } finally {
            setLoading(false);
        }
    };

    const features = [
        'Реалистичные диалоги с ИИ',
        'Персональная статистика',
        'Множество сценариев',
        'Анализ ваших ответов'
    ];

    return (
        <div className="min-h-screen flex">
            {/* Левая часть - форма */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-gray-50 dark:bg-gray-900">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex justify-center mb-8">
                        <img src="/buzilla_universal.svg" alt="Бузилла" className="w-20 h-20" />
                    </div>

                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Создать аккаунт</h2>
                        <p className="text-gray-600 dark:text-gray-400">Начните тренировку уже сегодня</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                            <p className="text-red-600 dark:text-red-400 text-sm text-center">{error}</p>
                        </div>
                    )}

                    <SocialAuthButtons />

                    <div className="my-6 flex items-center gap-4">
                        <div className="flex-1 h-px bg-gray-300 dark:bg-gray-700"></div>
                        <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">или</span>
                        <div className="flex-1 h-px bg-gray-300 dark:bg-gray-700"></div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="relative">
                            <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                required
                                placeholder="Имя пользователя"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div className="relative">
                            <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="email"
                                required
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div className="relative">
                            <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="password"
                                required
                                placeholder="Пароль"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>
                        <div className="relative">
                            <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="password"
                                required
                                placeholder="Подтвердите пароль"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-lg shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-6"
                        >
                            {loading ? (
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            ) : 'Зарегистрироваться'}
                        </button>
                    </form>

                    <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
                        Уже есть аккаунт?{' '}
                        <Link to="/login" className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">
                            Войти
                        </Link>
                    </p>
                </div>
            </div>

            {/* Правая часть - декоративная */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-20 left-20 w-40 h-40 bg-white/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
                </div>
                <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
                    <img src="/buzilla_universal.svg" alt="Бузилла" className="w-32 h-32 mb-8 drop-shadow-2xl" />
                    <h1 className="text-4xl font-bold mb-4 text-center">Начните прямо сейчас!</h1>
                    <p className="text-xl text-purple-100 text-center max-w-md mb-8">
                        Присоединяйтесь к тысячам пользователей, которые улучшают свои навыки общения
                    </p>
                    
                    <div className="space-y-4 w-full max-w-sm">
                        {features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3">
                                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                    <CheckIcon className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-white font-medium">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;
